/*
 * File: Routes.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:20 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 9, 2025 at 10:35 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React from 'react';

// Utilities
import { createLocaleVariants } from '../../utilities/locale';
import { filterProps, prepareComponent } from '../../utilities/routes';

// Navigation
import { PublicRouteWrapper, ContributorRouteWrapper } from '../../navigation';

// Components
import {
	ContributorDashboard,
	Error,
	FindStory,
	Home,
	Learn,
	Message,
	Question,
	Resource,
	ResourceLocale,
	ResourcePrompt,
	ShareStory,
	Story,
	Support
} from '../pages';

// Partner-Specific Components
import {
	MeTooAbout,
} from '../../../partners/platform/metoo/pages';
import {
	NoMoreAbout,
} from '../../../partners/platform/nomore/pages';
import {
	WeSpeakAbout,
	WeSpeakCommunityGuidelines,
	WeSpeakAccessibilityStatement,
	WeSpeakPrivacyPolicy,
	WeSpeakReviewGuidelines,
	WeSpeakTermsOfUse,
	WeSpeakCookieStatement
} from '../../../partners/platform/wespeak/pages';

// Constants
import { INTERNAL_CONTENT_TYPES, STORY_MEDIA_TYPES } from '../../../constants/base.constants';

/**
 * Component Collections
 */

const PartnerPages = {
	MeTooAbout,
	NoMoreAbout,
	WeSpeakAbout,
	WeSpeakCommunityGuidelines,
	WeSpeakAccessibilityStatement,
	WeSpeakPrivacyPolicy,
	WeSpeakReviewGuidelines,
	WeSpeakTermsOfUse,
	WeSpeakCookieStatement
};

/**
 * Routes
 */

export const Routes = (props) => {

	// Get current meta from props
	const metaObj = props?.meta;

	// Get current partner from props
	const partnerObj = props?.partner;

	// Get partner features
	const partnerFeatures = partnerObj?.features || {};

	// Check if application is core
	const isCore = partnerObj?.is_core !== false;

	// Render routes
	return [].concat(...[

		/**
		 * Base Pages
		 */

		prepareComponent(createLocaleVariants({
			path: '/',
			exact: true,
			shouldIndex: true,
			priority: '1.0',
			functions: [
				{
					function: 'feed/fetchFeed',
					restrictToPath: false,
					defaultParams: {
						page: 0,
						content_types: [
							INTERNAL_CONTENT_TYPES.STORY,
							INTERNAL_CONTENT_TYPES.MESSAGE,
							INTERNAL_CONTENT_TYPES.MANTRA,
							INTERNAL_CONTENT_TYPES.SUPPORT_IMAGE,
							INTERNAL_CONTENT_TYPES.SUPPORT_VIDEO
						],
						medium_types: [
							STORY_MEDIA_TYPES.NARRATIVE,
							STORY_MEDIA_TYPES.ARTWORK
						]
					}
				},
				{
					function: 'tag/fetchStoryTagsWithParameters',
					restrictToPath: false,
					objWrapper: 'storyTags',
					defaultParams: { limit_to_usage: false }
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<Home
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),
		partnerFeatures?.qa?.enabled && prepareComponent(createLocaleVariants({
			path: '/learn',
			exact: true,
			shouldIndex: true,
			priority: '0.8',
			functions: [
				{
					function: 'question/fetchQuestionPreviewsWithParameters',
					restrictToPath: false,
					objWrapper: 'questions'
				},
				{
					function: 'questionTag/fetchQuestionTagsWithParameters',
					restrictToPath: false,
					objWrapper: 'questionTags'
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<Learn
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),
		partnerFeatures?.support?.enabled && prepareComponent(createLocaleVariants({
			path: '/resources',
			exact: true,
			shouldIndex: true,
			priority: '0.8',
			functions: [
				{
					function: 'resource/fetchResourcesDirectory',
					restrictToPath: false,
					objWrapper: 'resources',
					defaultParams: { is_featured: false }
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<Support
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),


		/**
		 * Share Pages
		 */

		partnerFeatures?.stories?.enabled && prepareComponent(createLocaleVariants({
			path: '/share',
			exact: true,
			shouldIndex: true,
			priority: '0.8',
			functions: [
				{
					function: 'tag/fetchStoryTagsWithParameters',
					restrictToPath: false,
					objWrapper: 'storyTags',
					defaultParams: { limit_to_usage: false }
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<ShareStory
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),


		/**
		 * Find Pages
		 */

		(partnerFeatures?.stories?.enabled || partnerFeatures?.messages?.enabled) && prepareComponent(createLocaleVariants({
			path: '/find-story',
			exact: true,
			shouldIndex: true,
			priority: '0.8',
			functions: [],
			component(path) {
				return (
					<PublicRouteWrapper>
						<FindStory
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),


		/**
		 * Detail Pages
		 */

		partnerFeatures?.stories?.enabled && prepareComponent(createLocaleVariants({
			path: '/story/:slug',
			exact: true,
			shouldIndex: true,
			isDynamic: true,
			priority: '0.8',
			functions: [
				{
					function: 'story/fetchStoryWithParameters', restrictToPath: true
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<Story
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),
		partnerFeatures?.messages?.enabled && prepareComponent(createLocaleVariants({
			path: '/message/:slug',
			exact: true,
			shouldIndex: true,
			isDynamic: true,
			priority: '0.8',
			functions: [
				{
					function: 'message/fetchMessage',
					restrictToPath: true
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<Message
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),
		isCore && partnerFeatures?.qa?.enabled && prepareComponent(createLocaleVariants({
			path: '/answer/:slug',
			exact: true,
			shouldIndex: true,
			isDynamic: true,
			priority: '0.8',
			functions: [
				{
					function: 'question/fetchQuestionWithParameters',
					restrictToPath: true,
					objWrapper: 'question'
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<Question
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),
		isCore && partnerFeatures?.support?.enabled && prepareComponent(createLocaleVariants({
			path: '/resource/:slug',
			exact: true,
			shouldIndex: true,
			isDynamic: true,
			priority: '0.8',
			functions: [
				{
					function: 'resource/fetchResource',
					restrictToPath: true
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<Resource
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),
		isCore && partnerFeatures?.support?.enabled && prepareComponent(createLocaleVariants({
			path: '/resources/p/:slug',
			exact: true,
			shouldIndex: true,
			isDynamic: true,
			priority: '0.8',
			functions: [
				{
					function: 'resource/fetchResourcePrompt',
					restrictToPath: true
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<ResourcePrompt
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),
		isCore && partnerFeatures?.support?.enabled && prepareComponent(createLocaleVariants({
			path: '/resources/l/:country/:region',
			exact: true,
			shouldIndex: true,
			isDynamic: true,
			priority: '0.8',
			functions: [
				{
					function: 'resource/fetchResourceLocale',
					restrictToPath: true
				},
			],
			component(path) {
				return (
					<PublicRouteWrapper>
						<ResourceLocale
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</PublicRouteWrapper>
				);
			}
		})),


		/**
		 * Partner-Specific Pages
		 */

		!isCore && [].concat(...partnerObj.custom_routes.map(({ path: pagePath, component }) => {

			// Get functional component
			const Component = PartnerPages[component];

			// Render component
			return prepareComponent(createLocaleVariants({
				path: pagePath,
				exact: true,
				shouldIndex: true,
				priority: '0.8',
				component(path) {
					return (
						<PublicRouteWrapper>
							<Component
								{...filterProps(path, props)}
								meta={metaObj}
							/>
						</PublicRouteWrapper>
					);
				}
			}));
		})),


		/**
		 * Contributor Pages
		 */

		prepareComponent(createLocaleVariants({
			path: '/update/:aControl/:bControl',
			exact: true,
			shouldIndex: false,
			functions: [],
			component(path) {
				return (
					<ContributorRouteWrapper>
						<ContributorDashboard
							{...filterProps(path, props)}
							meta={metaObj}
						/>
					</ContributorRouteWrapper>
				);
			}
		})),


		/**
		 * Error
		 */

		prepareComponent({
			path: '*',
			shouldIndex: false,
			isError: true,
			code: 404,
			component(path) {
				return (
					<Error
						code={404}
						{...filterProps(path, props)}
						meta={{
							showSocial: false,
							shouldIndex: false,
							...metaObj
						}}
					/>
				);
			}
		})
	].filter(Boolean));
};
